/**
 * A supplier's transport tariffs (docs/suppliers.md, "Gewicht und Transporttarif"): dated like the discounts, each
 * priced per time unit of the trip (`TIME`, "je 15 min") or per trip (`TRIP`), by weight class. Nothing imported that
 * reaches Pinia: the supplier store reads it while the app starts.
 */
import { inForceOn } from "@/services/dated"

export const TRANSPORT_BASES = ["TIME", "TRIP"]

// What a new tariff starts with: per time unit of a quarter of an hour, one class for every weight.
export const DEFAULT_MINUTES_PER_UNIT = 15

// At most this many classes; the server refuses more.
export const MAX_TRANSPORT_CLASSES = 10

/** A tariff nobody has priced yet, from `validFrom` on: only its open class ("jedes Gewicht"), empty. */
export const blankTariff = validFrom => ({
  validFrom,
  basis: "TIME",
  minutesPerUnit: DEFAULT_MINUTES_PER_UNIT,
  classes: [{ upToTons: null, prices: {} }],
  tollIncluded: false,
  note: null,
})

/**
 * What one class price of a tariff is for: "je 15 min" (by time, its minutes), "je Fahrt"; by time without minutes
 * "je Zeiteinheit". `t` is the caller's, as this file imports nothing.
 */
export const perUnitText = (tariff, t) =>
  tariff?.basis === "TIME" && tariff.minutesPerUnit
    ? `${t("supplier.transport.per_time")} ${tariff.minutesPerUnit} ${t("supplier.transport.minutes")}`
    : t(`supplier.transport.per.${tariff?.basis}`)

/** The tariff in force on `day` (YYYY-MM-DD): the latest that has begun by then, or null. */
export const tariffOn = (tariffs, day) => inForceOn(tariffs, day)

const isOpen = transportClass => transportClass.upToTons == null || transportClass.upToTons === ""

/**
 * The class a machine of `weight` tons travels in, as the server picks it: the smallest bound it does not exceed (a
 * bound is inclusive), above the last bound the open class; null above every bound of a tariff without an open class.
 * Without a weight only a tariff of one open class has a class (every weight costs the same there).
 */
export function classFor(tariff, weight) {
  if (!tariff) return null
  const classes = tariff.classes ?? []
  if (weight == null || weight === "" || Number.isNaN(Number(weight))) {
    return classes.length === 1 && isOpen(classes[0]) ? classes[0] : null
  }
  const bounded = classes
    .filter(transportClass => !isOpen(transportClass))
    .sort((a, b) => Number(a.upToTons) - Number(b.upToTons))
  return (
    bounded.find(transportClass => Number(weight) <= Number(transportClass.upToTons)) ?? classes.find(isOpen) ?? null
  )
}

/** The highest bound of a tariff: what its open class starts above ("über 22 t"). Null without a bound. */
export function highestBound(tariff) {
  const bounds = (tariff?.classes ?? [])
    .filter(transportClass => !isOpen(transportClass))
    .map(it => Number(it.upToTons))
  return bounds.length ? Math.max(...bounds) : null
}

/** The cheapest class price of a tariff in `currency`, or null where no class has one there. */
export function cheapestPrice(tariff, currency) {
  const prices = (tariff?.classes ?? [])
    .map(transportClass => transportClass.prices?.[currency])
    .filter(price => price != null && price !== "")
    .map(Number)
  return prices.length ? Math.min(...prices) : null
}

/** The currency a tariff is priced in: `preferred` where a class has a price in it, else the first there is. */
export function tariffCurrency(tariff, preferred) {
  const currencies = (tariff?.classes ?? []).flatMap(transportClass => Object.keys(transportClass.prices ?? {}))
  return currencies.includes(preferred) ? preferred : (currencies[0] ?? preferred)
}

const numberOrNull = value => (value == null || value === "" || Number.isNaN(Number(value)) ? null : Number(value))

/** A tariff's figures as they compare, whatever day it holds from: an empty open class is none. */
const figures = tariff => ({
  basis: tariff.basis,
  minutesPerUnit: tariff.basis === "TIME" ? numberOrNull(tariff.minutesPerUnit) : null,
  classes: (tariff.classes ?? [])
    .map(transportClass => ({
      upToTons: numberOrNull(transportClass.upToTons),
      prices: Object.fromEntries(
        Object.entries(transportClass.prices ?? {})
          .map(([currency, price]) => [currency, numberOrNull(price)])
          .filter(([, price]) => price != null)
          .sort(([a], [b]) => a.localeCompare(b))
      ),
    }))
    .filter(transportClass => transportClass.upToTons != null || Object.keys(transportClass.prices).length),
  tollIncluded: Boolean(tariff.tollIncluded),
  note: tariff.note?.trim() || null,
})

/** Whether two tariffs ask the same, whatever day each holds from. */
export const sameTariff = (a, b) => JSON.stringify(figures(a)) === JSON.stringify(figures(b))

/** Whether a tariff is still as `blankTariff` starts it: no bound, no price, no note, nothing to send. */
export const isBlankTariff = tariff => sameTariff(tariff, blankTariff(tariff.validFrom))

/**
 * The tariffs as the server takes them: numbers, a time unit only per time, the classes rising with an empty open class
 * left out, a blank note as none. Null (the stored ones stay) for conditions that came without tariffs.
 */
export function tariffsRequest(tariffs) {
  if (tariffs == null) return null
  return tariffs.map(tariff => {
    const { basis, minutesPerUnit, classes, tollIncluded, note } = figures(tariff)
    const bounded = classes
      .filter(transportClass => transportClass.upToTons != null)
      .sort((a, b) => a.upToTons - b.upToTons)
    return {
      validFrom: tariff.validFrom,
      basis,
      minutesPerUnit,
      classes: [...bounded, ...classes.filter(transportClass => transportClass.upToTons == null)],
      tollIncluded,
      note,
    }
  })
}
